import { ReactNode, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { randomUuid, useLoader } from "mfe-lib-bfo-shared";
import { tokenManager } from "mfe-lib-bfo-shared/session";
import { usePreLoginHttp } from "mfe-lib-bfo-shared/query";
import _ from "lodash";
import { AppConfig } from "@/config/app.config";

// Routed through ms-bfo-gateway (see ms-bfo-gateway/application.yaml, route id
// "ms-bfo-auth-session-svc"), which forwards to BfoTokenController#channelRedirect
// in ms-bfo-auth-session-svc. Proxied to the gateway by this host's own vite dev
// server (see vite.config.ts) since there's no access token yet to attach.
interface ExchangeResult {
    accessToken: string;
    refreshToken: string;
    expiryTime: string;
    sessionRefreshCheckpointTime: string;
    absoluteTimeoutTime: string;
    errorCode?: string;
}

interface ChannelRedirectResponse {
    data: {
        accessToken: string;
        refreshToken: string;
        expiryTime: string;
        sessionRefreshCheckpointTime: string;
        absoluteTimeoutTime: string;
        error?: {
            errorCode?: string;
        };
    };
    statusDesc?: string;
}

interface ChannelRedirectGateProps {
    i18n: any;
    children: ReactNode;
}

/**
 * Exchanges the one-time `handshakeCode` for authentication tokens.
 *
 * The request is managed by TanStack Query so the result is cached by
 * handshakeCode. React 18 StrictMode can therefore reuse the same query
 * instead of making another exchange request.
 */
export function ChannelRedirectGate({ i18n, children }: ChannelRedirectGateProps) {
    const location = useLocation();
    const navigate = useNavigate();
    const { showLoader, hideLoader } = useLoader();

    const { api } = usePreLoginHttp();

    const [ready, setReady] = useState(false);

    const params = new URLSearchParams(location.search);
    const code = params.get("handshakeCode");

    const exchangeQuery: any = api.useApiQuery<ChannelRedirectResponse>(
        ["channel-redirect", code],
        {
            path: `${AppConfig.getChannelRedirectUrl()}/${encodeURIComponent(code)}`,
            method: "POST",
            headers: {
                "x-correlation-id": randomUuid(),
                "x-source-id": "mfe-bfo-credit-application",
                "x-source-country": "MY",
                "x-source-date-time": new Date().toISOString(),
            },
        },
        {
            enabled: !!code,
            staleTime: Infinity,
            gcTime: Infinity,
            //retry: false,
            refetchOnWindowFocus: false,
            refetchOnReconnect: false,
            refetchOnMount: false,
        },
    );

    useEffect(() => {
        // Update language if provided by the handshake
        const language = params.get("language");

        if (language && i18n.language !== language) {
            i18n.changeLanguage(language);
        }

        const body = exchangeQuery.data;

        const errorCode = () => {
            if (exchangeQuery?.error?.errorCode) {
                return exchangeQuery.error.errorCode
            } else if (body?.error?.errorCode) {
                return body.error.errorCode
            } else {
                return exchangeQuery.error?.details?.errorCode
            }
        }

        if (!code) {
            if (tokenManager.getToken("user")) {
                setReady(true);
                return;
            }

            if (!_.isEmpty(body)) {
                setReady(false);
                navigate("/error", { state: { errorCode: errorCode() }, replace: true });
            }

            return;
        }

        showLoader();

        if (exchangeQuery.isLoading) {
            return;
        }

        if (exchangeQuery.isError) {
            console.error("[ChannelRedirectGate] exchange failed:", exchangeQuery.error);

            navigate("/error", { state: { errorCode: errorCode() }, replace: true });
            hideLoader();
            return;
        }

        if (!exchangeQuery.data) {
            return;
        }

        if (!body?.data?.accessToken) {
            console.error("[ChannelRedirectGate] exchange failed:", body?.statusDesc);
            navigate("/error", { state: { errorCode: errorCode() }, replace: true });
            hideLoader();
            return;
        }

        const result: ExchangeResult = {
            accessToken: body.data.accessToken,
            refreshToken: body.data.refreshToken,
            expiryTime: body.data.expiryTime,
            sessionRefreshCheckpointTime: body.data.sessionRefreshCheckpointTime,
            absoluteTimeoutTime: body.data.absoluteTimeoutTime,
        };

        // Store authentication information
        sessionStorage.setItem("user.accessToken", result.accessToken);

        sessionStorage.setItem("user.refreshToken", result.refreshToken);

        sessionStorage.setItem("user.expiryTime", result.expiryTime);

        sessionStorage.setItem("user.sessionRefreshCheckpointTime", result.sessionRefreshCheckpointTime);

        sessionStorage.setItem("user.absoluteTimeoutTime", result.absoluteTimeoutTime);

        sessionStorage.setItem("user.hasActivitySinceRefresh", "false");

        params.delete("handshakeCode");
        params.delete("language");

        navigate(
            {
                pathname: location.pathname,
                search: params.toString(),
            },
            { replace: true },
        );

        setReady(true);
        hideLoader();
    }, [
        code,
        exchangeQuery.data,
        exchangeQuery.isError,
        exchangeQuery.isLoading,
        exchangeQuery.error,
        i18n,
        location.pathname,
        navigate,
        showLoader,
        hideLoader,
    ]);

    if (!ready) {
        return null;
    }

    return <>{children}</>;
}

export default ChannelRedirectGate;
